import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

/** Upload a generated PDF directly to a signed R2 PUT URL. */
export async function uploadGeneratedPdfToSignedUrl(
  uploadUrl: string,
  pdfData: ArrayBuffer,
): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    headers: {
      "Content-Type": "application/pdf",
    },
    body: pdfData,
  });

  if (!response.ok) {
    const details = await response.text().catch(() => "");
    throw new Error(
      details
        ? `R2 upload failed with status ${response.status}: ${details}`
        : `R2 upload failed with status ${response.status}`,
    );
  }
}

/**
 * Generate a PDF by rendering a URL in a hidden iframe, capturing with html2canvas,
 * and converting to PDF via jsPDF. Returns the PDF as an ArrayBuffer.
 */
export async function generateDocumentPdf(url: string): Promise<ArrayBuffer> {
  return new Promise<ArrayBuffer>((resolve, reject) => {
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.left = "-10000px";
    iframe.style.top = "0";
    iframe.style.width = "1200px";
    iframe.style.height = "1600px";
    iframe.style.border = "none";
    iframe.style.opacity = "0";
    iframe.style.pointerEvents = "none";

    const cleanup = () => {
      try {
        document.body.removeChild(iframe);
      } catch {
        // Already removed
      }
    };

    const timeout = window.setTimeout(() => {
      cleanup();
      reject(new Error(`PDF generation timed out for ${url}`));
    }, 30000);

    iframe.onload = async () => {
      try {
        // Wait for content to render
        await new Promise((r) => window.setTimeout(r, 2000));

        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!iframeDoc?.body) {
          throw new Error("Could not access iframe document");
        }

        // Hide any print-hide elements
        const printHideElements = Array.from(
          iframeDoc.querySelectorAll<HTMLElement>("[data-print-hide='true'], .print\\:hidden"),
        );
        for (const el of printHideElements) {
          el.style.display = "none";
        }

        const canvas = await html2canvas(iframeDoc.body, {
          scale: 2,
          useCORS: true,
          logging: false,
          windowWidth: 1200,
          backgroundColor: "#ffffff",
          // Allow cross-origin iframe content
          allowTaint: true,
        });

        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: "a4",
        });

        const pageWidth = 210;
        const pageHeight = 297;
        const imgHeight = (canvas.height * pageWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, "PNG", 0, position, pageWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft > 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, "PNG", 0, position, pageWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        const pdfArrayBuffer = pdf.output("arraybuffer");
        window.clearTimeout(timeout);
        cleanup();
        resolve(pdfArrayBuffer);
      } catch (error) {
        window.clearTimeout(timeout);
        cleanup();
        reject(error);
      }
    };

    iframe.onerror = () => {
      window.clearTimeout(timeout);
      cleanup();
      reject(new Error(`Failed to load ${url} in iframe`));
    };

    document.body.appendChild(iframe);
    iframe.src = url;
  });
}

/** Document types that can be generated */
export type GeneratableDocType = "bol" | "invoice" | "order_resume";

/** Build the URL for each document type */
export function getDocumentUrl(
  docType: GeneratableDocType,
  order: { _id?: string; id?: string; orderNumber?: string },
): string {
  const orderId = order._id ?? order.id;
  const orderNumber = order.orderNumber;

  switch (docType) {
    case "bol":
      return orderNumber
        ? `/bol?orderNumber=${orderNumber}`
        : `/bol?orderId=${orderId}`;
    case "invoice":
      return `/invoice/${orderId}`;
    case "order_resume":
      return orderNumber
        ? `/order-resume?orderNumber=${orderNumber}`
        : `/order-resume?orderId=${orderId}`;
  }
}

/** Human-readable label for document types */
export const DOC_TYPE_LABELS: Record<GeneratableDocType, string> = {
  bol: "BOL",
  invoice: "Invoice",
  order_resume: "Delivery Order",
};
